import React, { useState, useEffect, useCallback } from 'react';

const PlatformGame = () => {
  const [gameState, setGameState] = useState({
    player: { x: 50, y: 300, velY: 0, onGround: false, width: 20, height: 20 },
    score: 0,
    gameOver: false,
    gameStarted: false
  });

  const [keys, setKeys] = useState({});
  const [cameraX, setCameraX] = useState(0);

  // Configuración del juego
  const GRAVITY = 0.5;
  const JUMP_FORCE = -12;
  const MOVE_SPEED = 4;
  const GAME_WIDTH = 800;
  const GAME_HEIGHT = 600;

  // Plataformas estáticas
  const platforms = [
    { x: 0, y: 550, width: 200, height: 50 },
    { x: 250, y: 450, width: 150, height: 20 },
    { x: 450, y: 350, width: 100, height: 20 },
    { x: 600, y: 250, width: 120, height: 20 },
    { x: 800, y: 400, width: 100, height: 20 },
    { x: 950, y: 300, width: 150, height: 20 },
    { x: 1150, y: 200, width: 100, height: 20 },
    { x: 1300, y: 350, width: 120, height: 20 },
    { x: 1500, y: 450, width: 200, height: 20 },
    { x: 1750, y: 300, width: 100, height: 20 },
    { x: 1900, y: 500, width: 150, height: 50 }
  ];

  // Obstáculos (espinas)
  const obstacles = [
    { x: 300, y: 430, width: 20, height: 20 },
    { x: 500, y: 330, width: 20, height: 20 },
    { x: 850, y: 380, width: 20, height: 20 },
    { x: 1200, y: 180, width: 20, height: 20 },
    { x: 1550, y: 430, width: 20, height: 20 }
  ];

  // Monedas
  const coins = [
    { x: 300, y: 400, width: 15, height: 15, collected: false },
    { x: 500, y: 300, width: 15, height: 15, collected: false },
    { x: 700, y: 200, width: 15, height: 15, collected: false },
    { x: 1000, y: 250, width: 15, height: 15, collected: false },
    { x: 1200, y: 150, width: 15, height: 15, collected: false },
    { x: 1600, y: 400, width: 15, height: 15, collected: false }
  ];

  const [gameCoins, setGameCoins] = useState(coins);

  // Manejo de teclas
  useEffect(() => {
    const handleKeyDown = (e) => {
      setKeys(prev => ({ ...prev, [e.key]: true }));
    };

    const handleKeyUp = (e) => {
      setKeys(prev => ({ ...prev, [e.key]: false }));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Función de colisión
  const checkCollision = (rect1, rect2) => {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  };

  // Lógica principal del juego
  const gameLoop = useCallback(() => {
    if (!gameState.gameStarted || gameState.gameOver) return;

    setGameState(prevState => {
      const newState = { ...prevState };
      const player = { ...newState.player };

      // Movimiento horizontal
      if (keys['ArrowLeft'] || keys['a']) {
        player.x -= MOVE_SPEED;
      }
      if (keys['ArrowRight'] || keys['d']) {
        player.x += MOVE_SPEED;
      }

      // Salto
      if ((keys['ArrowUp'] || keys['w'] || keys[' ']) && player.onGround) {
        player.velY = JUMP_FORCE;
        player.onGround = false;
      }

      // Aplicar gravedad
      player.velY += GRAVITY;
      player.y += player.velY;

      // Comprobar colisiones con plataformas
      player.onGround = false;
      platforms.forEach(platform => {
        if (checkCollision(player, platform)) {
          if (player.velY > 0 && player.y < platform.y) {
            player.y = platform.y - player.height;
            player.velY = 0;
            player.onGround = true;
          }
        }
      });

      // Límites del mundo
      if (player.x < 0) player.x = 0;
      if (player.y > GAME_HEIGHT) {
        newState.gameOver = true;
      }

      // Comprobar colisiones con obstáculos
      obstacles.forEach(obstacle => {
        if (checkCollision(player, obstacle)) {
          newState.gameOver = true;
        }
      });

      // Comprobar colisiones con monedas
      setGameCoins(prevCoins => {
        const newCoins = prevCoins.map(coin => {
          if (!coin.collected && checkCollision(player, coin)) {
            newState.score += 100;
            return { ...coin, collected: true };
          }
          return coin;
        });
        return newCoins;
      });

      newState.player = player;
      return newState;
    });

    // Actualizar cámara
    setCameraX(prev => {
      const targetX = gameState.player.x - GAME_WIDTH / 2;
      return Math.max(0, targetX);
    });
  }, [gameState.gameStarted, gameState.gameOver, gameState.player.x, keys]);

  // Loop del juego
  useEffect(() => {
    const interval = setInterval(gameLoop, 16); // ~60 FPS
    return () => clearInterval(interval);
  }, [gameLoop]);

  const startGame = () => {
    setGameState({
      player: { x: 50, y: 300, velY: 0, onGround: false, width: 20, height: 20 },
      score: 0,
      gameOver: false,
      gameStarted: true
    });
    setGameCoins(coins.map(coin => ({ ...coin, collected: false })));
    setCameraX(0);
  };

  const resetGame = () => {
    startGame();
  };

  if (!gameState.gameStarted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-blue-400 to-blue-600 text-white">
        <div className="text-center mb-8">
          <h1 className="text-6xl font-bold mb-4">🏃‍♂️ Platform Runner</h1>
          <p className="text-xl mb-2">¡Salta entre plataformas y recolecta monedas!</p>
          <p className="text-lg opacity-90">Usa las flechas o WASD para moverte</p>
          <p className="text-lg opacity-90">Espacio o W para saltar</p>
        </div>
        <button
          onClick={startGame}
          className="px-8 py-4 bg-green-500 hover:bg-green-600 rounded-lg text-2xl font-bold transition-colors shadow-lg"
        >
          🎮 Comenzar Juego
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-b from-blue-400 to-blue-600">
      {/* HUD */}
      <div className="w-full max-w-4xl flex justify-between items-center p-4 text-white">
        <div className="text-2xl font-bold">💰 {gameState.score}</div>
        <div className="text-lg">Posición: {Math.floor(gameState.player.x)}</div>
      </div>

      {/* Área del juego */}
      <div 
        className="relative bg-gradient-to-b from-sky-300 to-green-300 border-4 border-white rounded-lg overflow-hidden shadow-2xl"
        style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
      >
        {/* Plataformas */}
        {platforms.map((platform, index) => (
          <div
            key={index}
            className="absolute bg-amber-800 border-2 border-amber-900 rounded-sm"
            style={{
              left: platform.x - cameraX,
              top: platform.y,
              width: platform.width,
              height: platform.height,
              display: platform.x - cameraX > -platform.width && platform.x - cameraX < GAME_WIDTH ? 'block' : 'none'
            }}
          />
        ))}

        {/* Obstáculos */}
        {obstacles.map((obstacle, index) => (
          <div
            key={index}
            className="absolute bg-red-600 border border-red-800 rounded-sm flex items-center justify-center"
            style={{
              left: obstacle.x - cameraX,
              top: obstacle.y,
              width: obstacle.width,
              height: obstacle.height,
              display: obstacle.x - cameraX > -obstacle.width && obstacle.x - cameraX < GAME_WIDTH ? 'block' : 'none'
            }}
          >
            <span className="text-xs">⚠️</span>
          </div>
        ))}

        {/* Monedas */}
        {gameCoins.map((coin, index) => (
          !coin.collected && (
            <div
              key={index}
              className="absolute bg-yellow-400 border-2 border-yellow-600 rounded-full flex items-center justify-center animate-pulse"
              style={{
                left: coin.x - cameraX,
                top: coin.y,
                width: coin.width,
                height: coin.height,
                display: coin.x - cameraX > -coin.width && coin.x - cameraX < GAME_WIDTH ? 'block' : 'none'
              }}
            >
              <span className="text-xs">💰</span>
            </div>
          )
        ))}

        {/* Jugador */}
        <div
          className="absolute bg-blue-500 border-2 border-blue-700 rounded-sm flex items-center justify-center transition-colors duration-100"
          style={{
            left: gameState.player.x - cameraX,
            top: gameState.player.y,
            width: gameState.player.width,
            height: gameState.player.height,
            backgroundColor: gameState.player.onGround ? '#3b82f6' : '#ef4444'
          }}
        >
          <span className="text-xs">🏃</span>
        </div>

        {/* Nubes de fondo */}
        <div className="absolute top-10 left-20 text-4xl opacity-60">☁️</div>
        <div className="absolute top-20 left-60 text-3xl opacity-40">☁️</div>
        <div className="absolute top-15 right-40 text-5xl opacity-50">☁️</div>
      </div>

      {/* Controles */}
      <div className="mt-4 text-white text-center">
        <p className="text-sm opacity-90">⬅️➡️ Mover | ⬆️/W/Espacio Saltar | Evita ⚠️ | Recolecta 💰</p>
      </div>

      {/* Game Over Modal */}
      {gameState.gameOver && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-red-600 mb-4">💀 Game Over</h2>
            <p className="text-2xl mb-2">Puntuación Final: <span className="font-bold text-green-600">{gameState.score}</span></p>
            <p className="text-lg mb-6">Distancia: {Math.floor(gameState.player.x)}m</p>
            <button
              onClick={resetGame}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold transition-colors"
            >
              🔄 Jugar de Nuevo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformGame;